import { useState, useCallback } from 'react';
import {
  ScoreEntrySimple,
  ScoreEntryAdvanced,
  ScoreEntryIntermediate,
} from '@aco/shared';
import type { Game, Round, PlayerStats } from '@aco/shared';
import { getPprPlayers } from '../utils/game-utils.js';

// ── Types ──────────────────────────────────────────────────

type CorrectionView =
  | { type: 'entry'; round: Round }
  | { type: 'confirm'; round: Round; newTeam1Score: number; newTeam2Score: number; newPlayerStats?: PlayerStats[] };

interface ScoreCorrectionFlowProps {
  game: Game;
  round: Round;
  onConfirm: (round: Round, newTeam1Score: number, newTeam2Score: number, playerStats?: PlayerStats[]) => Promise<void>;
  onDismiss: () => void;
}

/**
 * Handles the score-correction flow for a single round:
 *   1. Score entry overlay (simple / advanced / intermediate)
 *   2. Confirmation dialog ("Change scores for Round X?")
 *
 * Caller controls which round to correct; this component owns the internal
 * entry → confirm state machine.
 */
export function ScoreCorrectionFlow({ game, round, onConfirm, onDismiss }: ScoreCorrectionFlowProps) {
  const [view, setView] = useState<CorrectionView>({ type: 'entry', round });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pass the specific round number so doubles alternation is correct for
  // the round being corrected (not necessarily game.currentRound).
  const pprPlayers = getPprPlayers(game, 'team1', round.roundNumber);

  // ── Score entry submit (simple mode) ──────────────────────
  const handleSimpleSubmit = useCallback(
    (t1Score: number, t2Score: number) => {
      setView({ type: 'confirm', round, newTeam1Score: t1Score, newTeam2Score: t2Score });
    },
    [round],
  );

  // ── PPR submit (advanced / intermediate) ──────────────────
  const handlePprSubmit = useCallback(
    (playerStats: PlayerStats[]) => {
      const t1 = playerStats[0]?.totalPoints ?? 0;
      const t2 = playerStats[1]?.totalPoints ?? 0;
      setView({ type: 'confirm', round, newTeam1Score: t1, newTeam2Score: t2, newPlayerStats: playerStats });
    },
    [round],
  );

  // ── Confirm and save ──────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (view.type !== 'confirm') return;
    setIsSubmitting(true);
    try {
      await onConfirm(view.round, view.newTeam1Score, view.newTeam2Score, view.newPlayerStats);
      // onConfirm (GearMenu.handleCorrectRoundConfirm) calls onCloseAll — no local state cleanup needed
    } finally {
      setIsSubmitting(false);
    }
  }, [view, onConfirm]);

  // ── Render: score entry ───────────────────────────────────
  if (view.type === 'entry') {
    if (game.scoringMode === 'simple') {
      return (
        <ScoreEntrySimple
          team1Name={game.team1.players[0]?.name ?? 'Team 1'}
          team2Name={game.team2.players[0]?.name ?? 'Team 2'}
          team1Score={game.team1.score}
          team2Score={game.team2.score}
          onSubmit={handleSimpleSubmit}
          onDismiss={onDismiss}
          submitLabel="Update Round"
        />
      );
    }

    if (game.scoringMode === 'advanced') {
      return (
        <ScoreEntryAdvanced
          players={pprPlayers}
          roundNumber={round.roundNumber}
          onSubmit={handlePprSubmit}
          onDismiss={onDismiss}
          submitLabel="Update Round"
        />
      );
    }

    // intermediate
    return (
      <ScoreEntryIntermediate
        players={pprPlayers}
        roundNumber={round.roundNumber}
        onSubmit={handlePprSubmit}
        onDismiss={onDismiss}
        submitLabel="Update Round"
      />
    );
  }

  // ── Render: confirmation dialog ───────────────────────────
  // Use the same round-aware pprPlayers so doubles shows the correct A/B player
  // for the round being corrected, not always Player A.
  const team1Name = pprPlayers[0].name;
  const team2Name = pprPlayers[1].name;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.72)',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Card — sized for tablet readability; container-type:size lets children use cqh/cqw */}
      <div
        style={{
          height: '80dvh',
          width: '90vw',
          background: '#1a1a2e',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.14)',
          boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
          color: '#fff',
          fontFamily: "'Inter', sans-serif",
          containerType: 'size',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6cqh 6cqw',
          boxSizing: 'border-box',
        }}
      >
        {/* Title */}
        <h2
          style={{
            margin: 0,
            fontSize: '6cqh',
            fontWeight: 800,
            fontFamily: "'Inter', sans-serif",
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          Change scores for Round {round.roundNumber}?
        </h2>

        {/* Score comparison block */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '3cqh',
          }}
        >
          {/* Old scores */}
          <div
            style={{
              background: 'rgba(255,255,255,0.06)',
              borderRadius: '12px',
              padding: '3cqh 4cqw',
            }}
          >
            <div
              style={{
                fontSize: '2.5cqh',
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                opacity: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: '1cqh',
              }}
            >
              Current
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3cqh', fontFamily: "'Inter', sans-serif", opacity: 0.7 }}>
                  {team1Name}
                </div>
                <div style={{ fontSize: '8cqh', fontWeight: 800, fontFamily: "'Inter', sans-serif" }}>
                  {round.team1Score}
                </div>
              </div>
              <div style={{ fontSize: '4cqh', opacity: 0.4, fontFamily: "'Inter', sans-serif" }}>–</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3cqh', fontFamily: "'Inter', sans-serif", opacity: 0.7 }}>
                  {team2Name}
                </div>
                <div style={{ fontSize: '8cqh', fontWeight: 800, fontFamily: "'Inter', sans-serif" }}>
                  {round.team2Score}
                </div>
              </div>
            </div>
          </div>

          {/* New scores */}
          <div
            style={{
              background: 'rgba(37,99,235,0.18)',
              borderRadius: '12px',
              padding: '3cqh 4cqw',
              border: '1.5px solid rgba(37,99,235,0.5)',
            }}
          >
            <div
              style={{
                fontSize: '2.5cqh',
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                color: '#60a5fa',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: '1cqh',
              }}
            >
              New
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3cqh', fontFamily: "'Inter', sans-serif", opacity: 0.7 }}>
                  {team1Name}
                </div>
                <div
                  style={{
                    fontSize: '8cqh',
                    fontWeight: 800,
                    fontFamily: "'Inter', sans-serif",
                    color: '#60a5fa',
                  }}
                >
                  {view.newTeam1Score}
                </div>
              </div>
              <div style={{ fontSize: '4cqh', opacity: 0.4, fontFamily: "'Inter', sans-serif" }}>–</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3cqh', fontFamily: "'Inter', sans-serif", opacity: 0.7 }}>
                  {team2Name}
                </div>
                <div
                  style={{
                    fontSize: '8cqh',
                    fontWeight: 800,
                    fontFamily: "'Inter', sans-serif",
                    color: '#60a5fa',
                  }}
                >
                  {view.newTeam2Score}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: 'flex',
            gap: '4cqw',
            width: '100%',
          }}
        >
          <button
            onClick={onDismiss}
            disabled={isSubmitting}
            style={{
              flex: 1,
              height: '10cqh',
              minHeight: '56px',
              borderRadius: '12px',
              border: '1.5px solid rgba(255,255,255,0.25)',
              background: 'transparent',
              color: '#fff',
              fontWeight: 600,
              fontSize: '3.5cqh',
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer',
              touchAction: 'manipulation',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting}
            style={{
              flex: 1,
              height: '10cqh',
              minHeight: '56px',
              borderRadius: '12px',
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 700,
              fontSize: '3.5cqh',
              fontFamily: "'Inter', sans-serif",
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              touchAction: 'manipulation',
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            {isSubmitting ? 'Saving…' : 'Yes, Update'}
          </button>
        </div>
      </div>
    </div>
  );
}
